import React, { useEffect } from 'react';
import { ObservableLevelEnumValue } from '../../data/enums';
import { useObservationValue } from '../../hooks/useObservationValue';
import { Label } from './Label';
import { UnitModel } from '../../data/types';

type InputType = 'string' | 'integer' | 'float';
export type SingleLineValidation =
  | ''
  | 'BelowOrEqualZero'
  | 'InvalidInput'
  | null;

interface MachineHoursInputProps {
  title?: string;
  type?: InputType;
  onInfoClick?: () => void;
  onChange?: (value: string) => void;
  onDefaultLoad?: (value: string) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  tooltipKey?: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  placeholder?: string;
  useTooltip?: boolean;
  error?: string | null;
  valdiation?: (value: string) => boolean;
  defaultValue?: string;
  parentValue?: string;
  validationError?: SingleLineValidation;
  alwaysRunOnBlur?: boolean;
  unit?: UnitModel;
}

export const validateInput = (value: string, type: InputType): boolean => {
  if (type === 'string') return true;
  if (type === 'integer') return /^\d*$/.test(value);
  if (type === 'float') return /^\d*\.?\d*$/.test(value);
  return true;
};

export const SingleLineTextField: React.FC<MachineHoursInputProps> = ({
  title,
  type = 'float',
  error,
  onChange,
  tooltipKey,
  useTooltip = true,
  onDefaultLoad,
  dataLevel,
  entityId,
  entityExternalId,
  inspectionCategoryTypeId,
  inspectionId,
  path,
  placeholder = 'Enter value',
  valdiation,
  defaultValue,
  alwaysRunOnBlur,
  validationError,
  unit,
}) => {
  // Feedback for rejected keystrokes/pastes: silently discarding input made a
  // pasted "-500" simply vanish with no explanation.
  const [inputWarning, setInputWarning] = React.useState<string | null>(null);
  const field = useObservationValue<string>({
    inspectionId,
    inspectionCategoryId: inspectionCategoryTypeId,
    dataLevel,
    entityId,
    entityExternalId,
    path,
    unitId: unit?.id,
    unitName: unit?.name,
    defaultValue,
    onDefaultLoad,
  });
  const { value, setValue } = field;

  useEffect(() => {
    if (validationError) {
      if (
        validationError === 'BelowOrEqualZero' ||
        validationError === 'InvalidInput'
      ) {
        void field.remove(); // clear current value if Measurement below or equal 0
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (validateInput(newValue, type)) {
      setValue(newValue);
      setInputWarning(null);
    } else {
      setInputWarning(
        type === 'integer' ? 'Whole numbers only' : 'Numbers only',
      );
    }
  };

  const handleBlur = async () => {
    setInputWarning(null);
    // A numeric field can hold a non-numeric partial the validator allows while
    // typing - a lone "." (or "" which is already a clear). parseFloat(".") is
    // NaN, so committing it would persist garbage that syncs to the server and
    // breaks the wear calc. Clear it on blur instead of storing it.
    if (
      type !== 'string' &&
      value.trim() !== '' &&
      !Number.isFinite(Number(value))
    ) {
      setValue('');
      setInputWarning(
        type === 'integer' ? 'Whole numbers only' : 'Numbers only',
      );
      return;
    }
    if (valdiation && !valdiation(value)) {
      setValue('');
      return;
    }

    // flushNow: blur ends the burst - persist + invalidate immediately so the Action
    // Status header updates without waiting out the stacked debounces (measured ~2s lag).
    const success = await field.commit(value, { flushNow: true });

    if (success || alwaysRunOnBlur) {
      onChange?.(value);
    }
  };

  const getInputMode = (): 'text' | 'numeric' | 'decimal' => {
    switch (type) {
      case 'string':
        return 'text';
      case 'integer':
        return 'numeric';
      case 'float':
        return 'decimal';
      default:
        return 'text';
    }
  };

  return (
    <div className="w-full space-y-2 py-4">
      {title && (
        <Label
          titleType="fieldTitle"
          tooltipKey={useTooltip ? tooltipKey : undefined}
        >
          {title}
        </Label>
      )}
      <input
        type="text"
        inputMode={getInputMode()}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="w-full rounded-md border border-line px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand focus:outline-none"
      />

      {inputWarning && (
        <div className="text-sm text-amber-700" role="status">
          {inputWarning}
        </div>
      )}
      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
};
